import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/store/api/baseQuery';

// Feature flag type - matching admin-web
export type FeatureFlagType = Record<string, any>;

interface IApiResponse<T = any> {
  success: boolean;
  response?: T;
  message?: string;
}

interface UpdateFeatureFlagPayload {
  propertyId?: number;
  organizationId?: number;
  flags: FeatureFlagType;
}

// RTK Query API slice for feature flags - matching admin-web pattern
export const featureFlagsApi = createApi({
  reducerPath: 'featureFlagsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['featureFlags'],
  endpoints: (builder) => ({
    // List all feature flags
    listAllFeatureFlags: builder.query<IApiResponse, void>({
      query: () => ({
        url: '/api/v1/feature-flags/all',
        method: 'GET',
      }),
      providesTags: ['featureFlags'],
    }),

    // List feature flags for a specific property
    listFeatureFlags: builder.query<IApiResponse, { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: '/api/v1/feature-flags',
        method: 'GET',
        params: { propertyId },
      }),
      providesTags: ['featureFlags'],
    }),

    // Update feature flags
    updateFeatureFlags: builder.mutation<IApiResponse, UpdateFeatureFlagPayload>({
      query: ({ propertyId, organizationId, flags }) => ({
        url: '/api/v1/feature-flags',
        method: 'PUT',
        body: {
          propertyId,
          organizationId,
          flags,
        },
      }),
      invalidatesTags: ['featureFlags'],
    }),

    // Create feature flags
    createFeatureFlags: builder.mutation<IApiResponse, UpdateFeatureFlagPayload>({
      query: ({ propertyId, organizationId, flags }) => ({
        url: '/api/v1/feature-flags',
        method: 'POST',
        body: {
          propertyId,
          organizationId,
          flags,
        },
      }),
      invalidatesTags: ['featureFlags'],
    }),

    // Put all feature flags for a property
    putAllFeatureFlags: builder.mutation<
      IApiResponse,
      { propertyId: number; flags: Record<string, boolean> }
    >({
      query: (body) => ({
        url: '/api/v1/feature-flags/all',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['featureFlags'],
    }),

    // Get organization feature flags
    getOrganizationFeatureFlags: builder.query<any, number>({
      query: (organizationId) => ({
        url: `/api/v1/feature-flags/organization/${organizationId}`,
        method: 'GET',
      }),
      providesTags: ['featureFlags'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useLazyListFeatureFlagsQuery,
  useListAllFeatureFlagsQuery,
  useLazyListAllFeatureFlagsQuery,
  useUpdateFeatureFlagsMutation,
  useCreateFeatureFlagsMutation,
  usePutAllFeatureFlagsMutation,
  useGetOrganizationFeatureFlagsQuery,
  useLazyGetOrganizationFeatureFlagsQuery,
} = featureFlagsApi;

